import { useState, useEffect } from "react";
import {
  Building2, Save, CreditCard, FileText, Mail, Phone, MapPin,
  Globe, Banknote, AlertCircle, CheckCircle, Search, RefreshCw, Wrench
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EinstellungenTab({ adminToken }) {
  const [settings, setSettings] = useState({
    company_name: "",
    owner_name: "",
    street: "",
    zip_code: "",
    city: "",
    country: "Deutschland",
    email: "",
    phone: "",
    website: "",
    
    tax_number: "",
    is_kleinunternehmer: true,
    kleinunternehmer_text: "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.",
    
    bank_name: "",
    account_holder: "",
    iban: "",
    bic: "",
    
    paypal_email: "",
    paypal_me_link: "",
    
    payment_term_days: 14,
    invoice_prefix: "",
    email_sender_name: "Personal Fussballtraining",
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Invoice audit state
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [fixingInvoice, setFixingInvoice] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, [adminToken]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/settings`, {
        headers: { "X-Admin-Token": adminToken }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success("Einstellungen gespeichert");
        setHasChanges(false);
      } else {
        toast.error("Fehler beim Speichern");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
    setSaving(false);
  };

  // Invoice Audit Functions
  const runInvoiceAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/audit/check-inconsistencies`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setAuditData(data);
      
      if (data.success) {
        const issueCount = 
          data.issues.missing_address.count +
          data.issues.incomplete_address.count +
          data.issues.missing_due_date.count +
          data.issues.old_pricing.count +
          data.issues.missing_participant.count;
        
        if (issueCount > 0) {
          toast.warning(`${issueCount} Inkonsistenz(en) gefunden`);
        } else {
          toast.success("Alle Rechnungen sind konsistent");
        }
      }
    } catch (err) {
      toast.error("Audit fehlgeschlagen");
    }
    setAuditLoading(false);
  };

  const fixInvoiceData = async (invoiceId) => {
    setFixingInvoice(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/audit/fix-missing-data?invoice_id=${invoiceId}`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        // Refresh audit
        runInvoiceAudit();
      } else {
        toast.error(data.detail || "Korrektur fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
    setFixingInvoice(null);
  };

  // Check if required fields are missing
  const missingFields = [];
  if (!settings.owner_name && !settings.company_name) missingFields.push("Name/Unternehmen");
  if (!settings.street || !settings.zip_code || !settings.city) missingFields.push("Anschrift");
  if (!settings.email) missingFields.push("E-Mail");
  if (!settings.iban && !settings.paypal_me_link) missingFields.push("Zahlungsdaten");

  if (loading) {
    return <div className="p-8 text-center text-[#71717A]">Lade Einstellungen...</div>;
  }

  return (
    <div className="space-y-6" data-testid="einstellungen-tab">
      {/* Warning if missing required data */}
      {missingFields.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-yellow-500">Unvollständige Daten</p>
            <p className="text-sm text-[#A1A1AA]">
              Bitte ergänze folgende Angaben für vollständige Rechnungen: {missingFields.join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Company Info */}
      <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-[#c1ff72]" />
          Unternehmensdaten
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="company_name">Unternehmensname</Label>
            <Input
              id="company_name"
              value={settings.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
              placeholder="z.B. Aktas Personal Fussballtraining"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="owner_name">Inhaber / Name *</Label>
            <Input
              id="owner_name"
              value={settings.owner_name}
              onChange={(e) => handleChange("owner_name", e.target.value)}
              placeholder="Vor- und Nachname"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="street">Straße und Hausnummer *</Label>
            <Input
              id="street"
              value={settings.street}
              onChange={(e) => handleChange("street", e.target.value)}
              placeholder="Musterstraße 123"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="zip_code">PLZ *</Label>
            <Input
              id="zip_code"
              value={settings.zip_code}
              onChange={(e) => handleChange("zip_code", e.target.value)}
              placeholder="12345"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="city">Stadt *</Label>
            <Input
              id="city"
              value={settings.city}
              onChange={(e) => handleChange("city", e.target.value)}
              placeholder="Musterstadt"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="email">E-Mail *</Label>
            <Input
              id="email"
              type="email"
              value={settings.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="kontakt@beispiel.de"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="phone">Telefon</Label>
            <Input
              id="phone"
              value={settings.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="0123 456789"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={settings.website}
              onChange={(e) => handleChange("website", e.target.value)}
              placeholder="https://www.beispiel.de"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
        </div>
      </div>

      {/* Tax Info */}
      <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#c1ff72]" />
          Steuerliche Angaben
        </h3>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="tax_number">Steuernummer</Label>
            <Input
              id="tax_number"
              value={settings.tax_number}
              onChange={(e) => handleChange("tax_number", e.target.value)}
              placeholder="123/456/78901"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
            <p className="text-xs text-[#71717A] mt-1">
              Die Steuernummer wird auf der Rechnung angezeigt.
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
            <div>
              <Label htmlFor="is_kleinunternehmer" className="font-semibold">Kleinunternehmerregelung (§19 UStG)</Label>
              <p className="text-sm text-[#71717A]">
                Wenn aktiviert, wird keine Umsatzsteuer auf Rechnungen ausgewiesen.
              </p>
            </div>
            <Switch
              id="is_kleinunternehmer"
              checked={settings.is_kleinunternehmer}
              onCheckedChange={(checked) => handleChange("is_kleinunternehmer", checked)}
            />
          </div>

          {settings.is_kleinunternehmer && (
            <div>
              <Label htmlFor="kleinunternehmer_text">Hinweistext für Rechnungen</Label>
              <Textarea
                id="kleinunternehmer_text"
                value={settings.kleinunternehmer_text}
                onChange={(e) => handleChange("kleinunternehmer_text", e.target.value)}
                placeholder="Gemäß § 19 UStG wird keine Umsatzsteuer berechnet."
                className="mt-1 bg-[#0A0A0A] border-white/20"
                rows={2}
              />
              <p className="text-xs text-[#71717A] mt-1">
                Dieser Text wird auf jeder Rechnung angezeigt.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bank Details */}
      <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <Banknote className="w-5 h-5 text-[#c1ff72]" />
          Bankverbindung
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="account_holder">Kontoinhaber</Label>
            <Input
              id="account_holder"
              value={settings.account_holder}
              onChange={(e) => handleChange("account_holder", e.target.value)}
              placeholder="Vor- und Nachname"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="bank_name">Bank</Label>
            <Input
              id="bank_name"
              value={settings.bank_name}
              onChange={(e) => handleChange("bank_name", e.target.value)}
              placeholder="z.B. Sparkasse"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="iban">IBAN *</Label>
            <Input
              id="iban"
              value={settings.iban}
              onChange={(e) => handleChange("iban", e.target.value)}
              placeholder="DE00 0000 0000 0000 0000 00"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="bic">BIC</Label>
            <Input
              id="bic"
              value={settings.bic}
              onChange={(e) => handleChange("bic", e.target.value)}
              placeholder="ABCDEFGH"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
        </div>
      </div>

      {/* PayPal */}
      <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-[#c1ff72]" />
          PayPal
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="paypal_email">PayPal E-Mail</Label>
            <Input
              id="paypal_email"
              type="email"
              value={settings.paypal_email}
              onChange={(e) => handleChange("paypal_email", e.target.value)}
              placeholder="paypal@beispiel.de"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="paypal_me_link">PayPal.Me Link</Label>
            <Input
              id="paypal_me_link"
              value={settings.paypal_me_link}
              onChange={(e) => handleChange("paypal_me_link", e.target.value)}
              placeholder="https://paypal.me/deinname"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
        </div>
      </div>

      {/* Invoice Settings */}
      <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#c1ff72]" />
          Rechnungseinstellungen
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="invoice_prefix">Rechnungsnummer-Präfix</Label>
            <Input
              id="invoice_prefix"
              value={settings.invoice_prefix}
              onChange={(e) => handleChange("invoice_prefix", e.target.value)}
              placeholder="z.B. RE- (optional)"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
            <p className="text-xs text-[#71717A] mt-1">
              Beispiel: RE-2026-0001
            </p>
          </div>
          <div>
            <Label htmlFor="payment_term_days">Zahlungsziel (Tage)</Label>
            <Input
              id="payment_term_days"
              type="number"
              value={settings.payment_term_days}
              onChange={(e) => handleChange("payment_term_days", parseInt(e.target.value) || 14)}
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="email_sender_name">E-Mail Absendername</Label>
            <Input
              id="email_sender_name"
              value={settings.email_sender_name}
              onChange={(e) => handleChange("email_sender_name", e.target.value)}
              placeholder="Personal Fussballtraining"
              className="mt-1 bg-[#0A0A0A] border-white/20"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#71717A]">
          {hasChanges ? (
            <span className="text-yellow-500">Ungespeicherte Änderungen</span>
          ) : (
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Alle Änderungen gespeichert
            </span>
          )}
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving || !hasChanges}
          className="bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Wird gespeichert..." : "Einstellungen speichern"}
        </Button>
      </div>

      {/* Invoice Audit Section */}
      <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-[#c1ff72]" />
            Rechnungen prüfen
          </h3>
          <Button
            onClick={runInvoiceAudit}
            disabled={auditLoading}
            variant="outline"
            className="border-white/20"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${auditLoading ? 'animate-spin' : ''}`} />
            {auditLoading ? "Prüfung läuft..." : "Inkonsistenzen prüfen"}
          </Button>
        </div>
        
        <p className="text-sm text-[#71717A] mb-4">
          Prüft alle Rechnungen auf fehlende Adressen, falsche Empfänger, alte Preise und andere Inkonsistenzen.
        </p>

        {auditData && (
          <div className="space-y-4">
            {/* Settings Issues */}
            {auditData.settings_issues?.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="font-semibold text-yellow-500 mb-2">Einstellungen unvollständig</p>
                <ul className="text-sm text-[#A1A1AA] space-y-1">
                  {auditData.settings_issues.map((issue, i) => (
                    <li key={i}>• {issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Summary */}
            <div className="bg-white/5 rounded-lg p-3">
              <p className="text-sm">{auditData.summary}</p>
            </div>

            {/* Missing Address */}
            {auditData.issues?.missing_address?.count > 0 && (
              <div className="border border-red-500/30 rounded-lg p-3">
                <p className="font-semibold text-red-400 mb-2">
                  Fehlende Rechnungsadresse ({auditData.issues.missing_address.count})
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {auditData.issues.missing_address.items.map((item) => (
                    <div key={item.invoice_id} className="flex items-center justify-between bg-black/20 rounded p-2 text-sm">
                      <div>
                        <span className="font-mono text-[#c1ff72]">{item.invoice_number}</span>
                        <span className="mx-2 text-[#71717A]">•</span>
                        <span>{item.customer_name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => fixInvoiceData(item.invoice_id)}
                        disabled={fixingInvoice === item.invoice_id}
                      >
                        <Wrench className={`w-3 h-3 mr-1 ${fixingInvoice === item.invoice_id ? 'animate-spin' : ''}`} />
                        Auto-Fix
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Incomplete Address */}
            {auditData.issues?.incomplete_address?.count > 0 && (
              <div className="border border-orange-500/30 rounded-lg p-3">
                <p className="font-semibold text-orange-400 mb-2">
                  Unvollständige Adresse ({auditData.issues.incomplete_address.count})
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {auditData.issues.incomplete_address.items.map((item) => (
                    <div key={item.invoice_id} className="bg-black/20 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[#c1ff72]">{item.invoice_number}</span>
                        <span className="text-[#71717A]">{item.customer_name}</span>
                      </div>
                      <p className="text-xs text-orange-400 mt-1">Aktuell: {item.current_address}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Participant */}
            {auditData.issues?.missing_participant?.count > 0 && (
              <div className="border border-blue-500/30 rounded-lg p-3">
                <p className="font-semibold text-blue-400 mb-2">
                  Teilnehmer nicht separat gespeichert ({auditData.issues.missing_participant.count})
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {auditData.issues.missing_participant.items.map((item) => (
                    <div key={item.invoice_id} className="flex items-center justify-between bg-black/20 rounded p-2 text-sm">
                      <div>
                        <span className="font-mono text-[#c1ff72]">{item.invoice_number}</span>
                        <span className="mx-2 text-[#71717A]">•</span>
                        <span>{item.customer_name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => fixInvoiceData(item.invoice_id)}
                        disabled={fixingInvoice === item.invoice_id}
                      >
                        <Wrench className={`w-3 h-3 mr-1 ${fixingInvoice === item.invoice_id ? 'animate-spin' : ''}`} />
                        Auto-Fix
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Old Pricing */}
            {auditData.issues?.old_pricing?.count > 0 && (
              <div className="border border-yellow-500/30 rounded-lg p-3">
                <p className="font-semibold text-yellow-400 mb-2">
                  Alter Preis (69 EUR) ({auditData.issues.old_pricing.count})
                </p>
                <p className="text-sm text-[#71717A] mb-2">
                  Diese Rechnungen können über Buchhaltung / Preiskorrektur korrigiert werden.
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {auditData.issues.old_pricing.items.map((item) => (
                    <div key={item.invoice_id} className="bg-black/20 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[#c1ff72]">{item.invoice_number}</span>
                        <div>
                          <span className="text-red-400 line-through mr-2">{item.current_price} EUR</span>
                          <span className="text-green-400">{item.correct_price} EUR</span>
                        </div>
                      </div>
                      <p className="text-xs text-[#71717A]">{item.customer_name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Good */}
            {auditData.issues && 
             auditData.issues.missing_address.count === 0 &&
             auditData.issues.incomplete_address.count === 0 &&
             auditData.issues.missing_due_date.count === 0 &&
             auditData.issues.old_pricing.count === 0 &&
             auditData.issues.missing_participant.count === 0 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div>
                  <p className="font-semibold text-green-500">Alle Rechnungen konsistent</p>
                  <p className="text-sm text-[#71717A]">Keine Inkonsistenzen gefunden.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
